import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { allElements } from "constants/constants";
import { OeeSummaryDashboardContext } from "contexts/OeeSummaryDashboardContext/OeeSummaryDashboardContext";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { toProductionDateRange } from "utilities/oeeDateRange";
import { IUseOeeSummaryFilterOptionsReturn } from "./useOeeSummaryFilterOptionsInterfaces";

/**
 * Machine center and shift options of the report: only the ones with OEE data in the selected range,
 * the machine centers further scoped to the production line clicked on the page. The filters panel
 * offers them and useAppliedMachineCentersGuard prunes against them, so both read the same list.
 */
const useOeeSummaryFilterOptions = (): IUseOeeSummaryFilterOptionsReturn => {
    const { dateRange, filters } = useContext(OeeSummaryDashboardContext);
    const { millId } = useParams();
    const { t } = useTranslation();
    const productionDateRange = useMemo(() => toProductionDateRange(dateRange), [dateRange]);
    const { loading, machineCenters, shifts } = MillsEntity.GetOeeSummaryDashboardFilterOptions(
        Number(millId),
        productionDateRange,
    );

    const allOption = useMemo(() => ({ id: allElements, name: t("locations.summary_report.filters.all") }), [t]);

    const machineCentersOptions = useMemo(
        () => [
            allOption,
            ...machineCenters
                .filter(
                    (machineCenter) =>
                        filters.productionLine === allElements ||
                        machineCenter.productionLineId === filters.productionLine,
                )
                .map((machineCenter) => ({ id: machineCenter.machineCenterId, name: machineCenter.name })),
        ],
        [allOption, machineCenters, filters.productionLine],
    );

    const shiftsOptions = useMemo(
        () => [allOption, ...shifts.map((shift) => ({ id: shift.shiftId, name: shift.name }))],
        [allOption, shifts],
    );

    return { loading, machineCentersOptions, shiftsOptions };
};

export default useOeeSummaryFilterOptions;
